import { HttpError, paginateLocally } from "@nasam/kernel";
import type {
  AssetAssignment,
  AssetAssignmentAsset,
  AssetAssignmentBranch,
  AssetAssignmentSortBy,
  AssetAssignmentsRepository,
} from "../domain";
import {
  assetAssignmentAssetFixtures,
  assetAssignmentBranchFixtures,
  assetAssignmentFixtures,
} from "../testing";

const DEFAULT_LIMIT = 10;

export interface MockAssetAssignmentsRepositoryOptions {
  readonly assignments?: readonly AssetAssignment[];
  /** Every asset in the company -- what `assign` can move. */
  readonly assets?: readonly AssetAssignmentAsset[];
  readonly branches?: readonly AssetAssignmentBranch[];
  /** Simulated network latency, so loading states show in the app. */
  readonly latencyMs?: number;
  readonly now?: () => Date;
}

function httpError(status: number, message: string): HttpError {
  return new HttpError(message, {
    status,
    code: undefined,
    method: "PATCH",
    url: "/assets/branch-assignment",
    data: undefined,
  });
}

function includes(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
}

// Same fields the real search matches: asset name or code, branch name.
function matches(assignment: AssetAssignment, keyword: string): boolean {
  return (
    includes(assignment.branch.nameEn, keyword) ||
    includes(assignment.branch.nameAr, keyword) ||
    includes(assignment.asset.nameEn, keyword) ||
    includes(assignment.asset.nameAr, keyword) ||
    includes(assignment.asset.code, keyword)
  );
}

function compare(
  a: AssetAssignment,
  b: AssetAssignment,
  sortBy: AssetAssignmentSortBy,
): number {
  switch (sortBy) {
    case "branchNameEn":
      return a.branch.nameEn.localeCompare(b.branch.nameEn, "en");
    case "branchNameAr":
      return a.branch.nameAr.localeCompare(b.branch.nameAr, "ar");
    case "assetNameEn":
      return a.asset.nameEn.localeCompare(b.asset.nameEn, "en");
    case "assetNameAr":
      return a.asset.nameAr.localeCompare(b.asset.nameAr, "ar");
    case "assignedAt":
      return Date.parse(a.assignedAt) - Date.parse(b.assignedAt);
  }
}

/** In-memory stand-in for tests and as a manual USE_MOCK fallback (see
 *  fleetMonitoringFeature.ts). Like the real API, an asset sits in exactly
 *  one branch, and assigning moves it. */
export function createMockAssetAssignmentsRepository({
  assignments: seed = assetAssignmentFixtures,
  assets = assetAssignmentAssetFixtures,
  branches = assetAssignmentBranchFixtures,
  latencyMs = 0,
  now = () => new Date(),
}: MockAssetAssignmentsRepositoryOptions = {}): AssetAssignmentsRepository {
  let assignments: readonly AssetAssignment[] = seed;
  let nextId = seed.length + 1;

  const wait = () =>
    latencyMs > 0
      ? new Promise<void>((resolve) => setTimeout(resolve, latencyMs))
      : Promise.resolve();

  return {
    async list(query = {}) {
      await wait();
      const keyword = query.search?.trim();
      const from =
        query.fromAssignedAt === undefined
          ? undefined
          : Date.parse(query.fromAssignedAt);
      const to =
        query.toAssignedAt === undefined
          ? undefined
          : Date.parse(query.toAssignedAt);
      // The API's default: createdAt desc.
      const sortBy = query.sortBy ?? "assignedAt";
      const direction = (query.sort ?? "desc") === "desc" ? -1 : 1;

      const rows = assignments
        .filter((assignment) => {
          const assignedAt = Date.parse(assignment.assignedAt);
          return (
            (!keyword || matches(assignment, keyword)) &&
            (from === undefined || assignedAt >= from) &&
            (to === undefined || assignedAt <= to)
          );
        })
        .toSorted((a, b) => compare(a, b, sortBy) * direction);

      return paginateLocally(rows, query.page ?? 1, query.limit ?? DEFAULT_LIMIT);
    },

    async assign(input) {
      await wait();
      const branch = branches.find((candidate) => candidate.id === input.branchId);
      if (branch === undefined) throw httpError(404, "Branch not found");

      const moving = input.assetIds.map((id) => {
        const asset = assets.find((candidate) => candidate.id === id);
        if (asset === undefined) throw httpError(404, "Asset not found");
        return asset;
      });
      // All-or-nothing, like the real transaction.
      const alreadyThere = assignments.some(
        (assignment) =>
          assignment.branch.id === branch.id &&
          input.assetIds.includes(assignment.asset.id),
      );
      if (alreadyThere) {
        throw httpError(409, "The Asset is already assigned to this Branch.");
      }

      const createdAt = now().toISOString();
      const created = moving.map((asset) => ({
        id: `asset-assignment-${String(nextId++)}`,
        branch,
        asset,
        assignedAt: createdAt,
      }));
      // Each asset's previous assignment ends.
      assignments = [
        ...created,
        ...assignments.filter(
          (assignment) => !input.assetIds.includes(assignment.asset.id),
        ),
      ];
      return {
        assignments: created,
        message: "Assets assigned to the Branch successfully.",
      };
    },
  };
}
